import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlayerProfile, SlotDemoScene } from '@goodtime/engine';
import type { RoadState } from '@goodtime/shared';
import { funnelBeacon } from '../api/funnel';
import { pickTrailBox } from '../api/slot';
import { giftTrail, giftWaiting } from '../components/journey/roadGift';
import { useTrailGame } from './useTrailGame';

/** How long after the spin's reels and wins the gift opens: the road rolls
 *  to the gift square first. */
const OPEN_AFTER_MS = 1200;

/** The gift square's board: its own title, and no level to move on to. */
const GIFT_WORDS = { title: 'GIFT', finishLabel: 'CARRY ON' };

export interface RoadGiftOptions {
  sessionId: string | null;
  playerId: string | null;
  themeId: string | null;
  road: RoadState | null;
  /** Changes with each spin's road (a gift reached on it is announced). */
  spinKey: number;
  getScene: () => SlotDemoScene | null;
  /** Gems a game pays on opening, and a box of gems, show on the profile. */
  profile: Pick<PlayerProfile, 'awardGems'>;
  /** A run, a game, a bonus or the reels are on: the gift waits. */
  paused: boolean;
  /** The gift has closed (played, or put off): fetch the road again. */
  onClosed: () => void;
}

/**
 * The gift square on every road (decision 11): reaching it opens the box
 * picker, a Scratch Card or a Lucky Wheel, as the Sweet Trail's gift does,
 * played on the slot's canvas to its end before the next spin. It opens by
 * itself once the road has rolled to it, or when a slot opens with one
 * waiting (a game left open after a reload opens again), and again on a tap
 * on the road after it couldn't open. Shown whenever the road has a gift,
 * even once the gift square has gone from the player's laps.
 */
export function useRoadGift({ sessionId, playerId, themeId, road, spinKey, getScene, profile, paused, onClosed }: RoadGiftOptions) {
  const game = useTrailGame({ sessionId, playerId, themeId, getScene, profile });
  const [playing, setPlaying] = useState(false);
  const playingRef = useRef(false);
  /** The gift last opened by itself: it doesn't open by itself again. */
  const opened = useRef<string | null>(null);
  const live = useRef({ sessionId, themeId, road, getScene, profile, onClosed, play: game.play });
  live.current = { sessionId, themeId, road, getScene, profile, onClosed, play: game.play };
  // Each time a slot opens, a gift still waiting there opens by itself again
  // (one that couldn't open last time, say).
  useEffect(() => {
    opened.current = null;
  }, [themeId]);

  const open = useCallback(async () => {
    const { sessionId: sid, themeId: theme, road: now } = live.current;
    // This slot's own scene: the last slot's can still be up as it opens.
    for (let i = 0; i < 40 && live.current.getScene()?.themeId !== theme; i++) await new Promise((r) => setTimeout(r, 250));
    const scene = live.current.getScene();
    if (playingRef.current || !sid || !theme || !scene || scene.themeId !== theme || !giftWaiting(now)) return;
    if (live.current.themeId !== theme) return;
    playingRef.current = true;
    setPlaying(true);
    opened.current = giftKey(theme, now!);
    try {
      // Not over a card or a hello already up.
      while (scene.boardOpen && live.current.getScene() === scene) await new Promise((r) => setTimeout(r, 250));
      if (live.current.getScene() !== scene || live.current.themeId !== theme) return;
      const result = await live.current.play(giftTrail(theme, live.current.road ?? now!));
      if (result && 'boxes' in result) {
        await scene.playTrailBonus(
          result.boxes,
          async (index) => {
            const res = await pickTrailBox(sid, theme, index);
            const picked = res.trail.bonus?.picks.find((p) => p.index === index);
            if (picked?.kind === 'GEMS') live.current.profile.awardGems(picked.amount);
            return { trail: res.trail, coins: res.wallet.coins };
          },
          GIFT_WORDS,
        );
      }
    } finally {
      playingRef.current = false;
      setPlaying(false);
      live.current.onClosed();
    }
  }, []);

  // A spin that reached the gift square says so (the portal counts gifts
  // with the trail's bonuses).
  const announced = useRef(0);
  useEffect(() => {
    if (!road || !themeId || spinKey === announced.current) return;
    announced.current = spinKey;
    if (road.trail.steps.some((s) => s.square === 'gift')) {
      funnelBeacon('trail_bonus', { playerId, detail: `${themeId}:${road.trail.level}:gift` });
    }
  }, [road, spinKey, themeId, playerId]);

  // Opens by itself once per gift, when nothing else is on.
  const waiting = giftWaiting(road);
  const key = road && themeId && waiting ? giftKey(themeId, road) : null;
  useEffect(() => {
    if (!key || paused || playingRef.current || opened.current === key) return;
    const timer = setTimeout(() => void open(), OPEN_AFTER_MS);
    return () => clearTimeout(timer);
  }, [key, paused, open]);

  // A tap on the road while the gift waits opens it again.
  useEffect(() => {
    const scene = getScene();
    if (scene) scene.onRoadGiftTap = () => void open();
  });

  // The gift couldn't open: the road says so.
  useEffect(() => {
    const scene = live.current.getScene();
    if (game.notice && scene?.themeId === live.current.themeId) scene.sayOnRoad(game.notice);
  }, [game.notice]);

  return { playing };
}

/** One gift: the slot, the lap, and its game once one is open. */
function giftKey(themeId: string, road: RoadState): string {
  return `${themeId}:${road.trail.level}:${road.giftGame?.playId ?? road.gift?.game ?? 'boxes'}`;
}
